"use client";
import React, { useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import { SentryError } from "@stadium/services/error-reporting/utils";

class ReCaptchaError extends SentryError {
  get name() {
    return "ReCaptcha Error";
  }
}

const captureReCaptchaError = (error) => {
  throw new ReCaptchaError(error);
};

export const useGoogleReCaptcha = (options) => {
  const recaptchaRef = useRef();

  const captchaElement = <ReCAPTCHA ref={recaptchaRef} size="invisible" {...options} />;

  const executeAsync = async () => {
    try {
      if (!recaptchaRef.current) {
        return null;
      }
      return await recaptchaRef.current.executeAsync();
    } catch (error) {
      captureReCaptchaError(error);
    }
  };

  const reset = () => {
    try {
      if (!recaptchaRef.current) {
        return;
      }
      recaptchaRef.current.reset();
    } catch (error) {
      captureReCaptchaError(error);
    }
  };

  const getCaptchaContainer = () => {
    try {
      const captchaNodes = document.querySelectorAll(
        "iframe[title='recaptcha challenge expires in two minutes']",
      );

      if (!captchaNodes || captchaNodes.length === 0) {
        return null;
      }

      const captcha = captchaNodes[captchaNodes.length - 1];
      const container = captcha?.closest("div[style*='visibility']");

      if (!container) {
        return null;
      }

      return container;
    } catch (error) {
      captureReCaptchaError(error);
    }
  };

  useEffect(() => {
    try {
      const styleTag = document.createElement("style");
      styleTag.innerHTML = ".grecaptcha-badge {visibility: hidden !important;}";
      document.body.appendChild(styleTag);

      return () => {
        try {
          if (document.body.contains(styleTag)) {
            document.body.removeChild(styleTag);
          }
        } catch (error) {
          captureReCaptchaError(error);
        }
      };
    } catch (error) {
      captureReCaptchaError(error);
    }
  }, []);

  const submitAsyncValidator = async () => {
    try {
      await executeAsync();
      reset();
      return true;
    } catch (error) {
      captureReCaptchaError(error);
    }
  };

  return {
    executeAsync,
    reset,
    captchaElement,
    getCaptchaContainer,
    submitAsyncValidator,
  };
};
